import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { QuickBooksExportSection } from "@/components/QuickBooksExportSection";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import {
  Users,
  UserPlus,
  UserCheck,
  FileText,
  Calendar,
  ArrowRight,
  AlertTriangle,
  ChevronRight,
  Check,
  Eye,
  MessageSquare,
  Mail,
  Phone,
  Bell,
  Fingerprint,
  Clock,
  PlayCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIE_COLORS = ["#10b981", "#f59e0b", "#f97316", "#64748b", "#94a3b8", "#ef4444"];

const StatCard = ({ title, value, icon: Icon, color, link }) => (
  <Link to={link || "#"} className="stat-card group">
    <div className="flex items-start justify-between">
      <div>
        <p className="stat-value">{value}</p>
        <p className="stat-label">{title}</p>
      </div>
      <div className={`stat-icon ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </Link>
);

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="font-medium text-slate-900">{payload[0].name}</p>
        <p className="text-sm text-slate-600">{payload[0].value} clients</p>
      </div>
    );
  }
  return null;
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [stats, setStats] = useState(null);
  const [potentialClients, setPotentialClients] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, potentialRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/dashboard/enhanced-stats`),
        axios.get(`${API}/dashboard/potential-clients?limit=5`),
        axios.get(`${API}/appointments?status=scheduled&include_assessments=true`),
      ]);
      setStats(statsRes.data);
      setPotentialClients(potentialRes.data);
      
      // Filter to only show RN assessments (not regular caregiver visits)
      const rnVisits = appointmentsRes.data.filter(appt => 
        appt.is_assessment || 
        appt.appointment_type === 'assessment' ||
        appt.appointment_type === 'rn_assessment' ||
        appt.appointment_type === 'supervisory_visit' ||
        appt.appointment_type === 'follow_up'
      );
      setUpcomingAppointments(rnVisits.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      // Fallback to basic stats if enhanced not available
      try {
        const basicStats = await axios.get(`${API}/dashboard/stats`);
        setStats({
          ...basicStats.data,
          status_distribution: [],
          weekly_trend: [],
          referral_funnel: [],
        });
      } catch (e) {
        console.error("Failed to fetch basic stats:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleAcknowledgeAlert = async (alertType, relatedIds) => {
    try {
      await axios.post(`${API}/dashboard/alerts/acknowledge`, {
        alert_type: alertType,
        related_ids: relatedIds || []
      });
      toast.success("Alert acknowledged");
      // Refresh stats to update alert status
      fetchDashboardData();
    } catch (error) {
      console.error("Failed to acknowledge alert:", error);
      toast.error("Failed to acknowledge alert");
    }
  };

  const handleResumeServices = async (orderId) => {
    try {
      const res = await axios.post(`${API}/md-orders/${orderId}/resume-services`);
      toast.success(
        res.data?.client_reactivated
          ? "Services resumed — client moved back to Active"
          : "Services marked as resumed"
      );
      fetchDashboardData();
    } catch (error) {
      console.error("Failed to resume services:", error);
      toast.error(error.response?.data?.detail || "Failed to mark services resumed");
    }
  };

  const handleUnacknowledgeAlert = async (alertType) => {
    try {
      await axios.delete(`${API}/dashboard/alerts/acknowledge/${alertType}`);
      toast.success("Acknowledgement removed");
      // Refresh stats to update alert status
      fetchDashboardData();
    } catch (error) {
      console.error("Failed to remove acknowledgement:", error);
      toast.error("Failed to remove acknowledgement");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Agency Name Header */}
      <div className="mb-6 flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
        <img 
          src="/carepilot-logo.png" 
          alt="CarePilot" 
          className="h-24 w-auto"
        />
        <div className="border-l-2 border-blue-200 pl-4">
          <h2 className="text-2xl font-bold text-blue-800">{user?.agency_name || "Your Agency"}</h2>
          <p className="text-sm text-slate-500">Powered by CarePilot</p>
        </div>
      </div>

      {/* Page Header */}
      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Welcome back! Here is your agency overview.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {hasPermission("referrals", "create") && (
            <Link to="/referrals">
              <Button className="gap-2" data-testid="new-referral-btn">
                <FileText className="w-4 h-4" />
                New Referral
              </Button>
            </Link>
          )}
          {hasPermission("schedule") && (
            <Link to="/schedule">
              <Button variant="outline" className="gap-2" data-testid="view-schedule-btn">
                <Calendar className="w-4 h-4" />
                View Schedule
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Field Mode banner - visible to nurses / case managers doing home visits */}
      {["rn", "case_manager"].includes((user?.role || "").toLowerCase()) && (
        <Link to="/caregiver" className="block mb-6" data-testid="field-mode-banner">
          <div className="rounded-xl border border-teal-200 bg-gradient-to-r from-teal-500 to-teal-600 text-white p-5 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Fingerprint className="w-6 h-6" />
              </div>
              <div>
                <p className="text-lg font-semibold leading-tight">Going out on a visit?</p>
                <p className="text-sm text-white/80">
                  Switch to Field Mode to clock in, GPS-verify your location, and capture care plan tasks & signatures.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap text-sm font-medium bg-white/15 hover:bg-white/25 px-4 py-2 rounded-lg">
              Open Field Mode
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
      )}

      {/* Stats Grid - Show cards based on user permissions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {hasPermission("clients") && (
          <StatCard
            title="Total Clients"
            value={stats?.total_clients || 0}
            icon={Users}
            color="bg-primary/10 text-primary"
            link="/clients"
          />
        )}
        {hasPermission("clients") && (
          <StatCard
            title="Active Clients"
            value={stats?.active_clients || 0}
            icon={UserCheck}
            color="bg-emerald-100 text-emerald-600"
            link="/clients?status=active"
          />
        )}
        {hasPermission("schedule") && (
          <StatCard
            title="Upcoming Visits"
            value={stats?.upcoming_appointments || 0}
            icon={Calendar}
            color="bg-blue-100 text-blue-600"
            link="/schedule"
          />
        )}
        {hasPermission("referrals") && (
          <StatCard
            title="Pending Referrals"
            value={stats?.pending_referrals || 0}
            icon={FileText}
            color="bg-amber-100 text-amber-600"
            link="/referrals"
          />
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Client Status Distribution - visible only if user has clients permission */}
        {hasPermission("clients") && (
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="client-distribution-chart">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Client Distribution</h2>
            {stats?.status_distribution && stats.status_distribution.some(d => d.value > 0) ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.status_distribution.filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {stats.status_distribution.filter(d => d.value > 0).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                <p>Add clients to see distribution</p>
              </div>
            )}
          </div>
        )}

        {/* Alerts */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Alerts</h2>
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          {stats?.alerts && stats.alerts.filter(a => !a.acknowledged).length > 0 ? (
            <div className="space-y-2">
              {/* Only show unacknowledged alerts */}
              {stats.alerts.filter(a => !a.acknowledged).map((alert, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border transition-all ${
                    alert.severity === 'high' 
                      ? 'bg-red-50 border-red-200' 
                      : alert.severity === 'medium'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                  data-testid={`alert-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-1.5 ${
                      alert.severity === 'high' 
                        ? 'bg-red-500' 
                        : alert.severity === 'medium'
                        ? 'bg-amber-500'
                        : 'bg-blue-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={alert.link || "#"}
                        className={`text-sm font-medium hover:underline ${
                          alert.severity === 'high' 
                            ? 'text-red-800' 
                            : alert.severity === 'medium'
                            ? 'text-amber-800'
                            : 'text-blue-800'
                        }`}
                      >
                        {alert.title}
                      </Link>
                        <p className="text-xs text-slate-600 mt-0.5 truncate" title={alert.description}>
                          {alert.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {alert.alert_type?.startsWith("hold_return_due:") && (
                          <Button
                            size="sm"
                            className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={(e) => {
                              e.preventDefault();
                              handleResumeServices(alert.related_ids?.[0]);
                            }}
                            title="Confirm the client is back and services have restarted"
                            data-testid={`resume-services-${alert.related_ids?.[0]}`}
                          >
                            <PlayCircle className="w-3.5 h-3.5 mr-1" />
                            Services Resumed
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs hover:bg-white/50"
                          onClick={(e) => {
                            e.preventDefault();
                            handleAcknowledgeAlert(alert.alert_type, alert.related_ids);
                          }}
                          title="Mark as seen"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          Acknowledge
                        </Button>
                        <Link to={alert.link || "#"}>
                          <ChevronRight className={`w-4 h-4 ${
                            alert.severity === 'high' 
                              ? 'text-red-400' 
                              : alert.severity === 'medium'
                              ? 'text-amber-400'
                              : 'text-blue-400'
                          }`} />
                        </Link>
                      </div>
                    </div>
                  </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-slate-400">
              <AlertTriangle className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">No alerts at this time</p>
              <p className="text-xs mt-1">All systems running smoothly</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Potential Clients - Workflow Progress - visible only if user has referrals permission */}
        {hasPermission("referrals") && (
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6" data-testid="potential-clients-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Potential Clients</h2>
                <p className="text-sm text-slate-500">Track referral workflow progress</p>
              </div>
              <Link
                to="/referrals"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                View all referrals
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {potentialClients.length === 0 ? (
              <div className="empty-state py-8">
                <div className="empty-state-icon">
                  <UserPlus className="w-8 h-8 text-slate-400" />
                </div>
                <p className="empty-state-title">No potential clients</p>
                <p className="empty-state-description">
                  Create a new referral to add potential clients.
                </p>
                {hasPermission("referrals", "create") && (
                  <Link to="/referrals" className="mt-4">
                    <Button size="sm" data-testid="empty-new-referral-btn">
                      <FileText className="w-4 h-4 mr-2" />
                      New Referral
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {potentialClients.map((client) => (
                  <Link
                    key={client.id}
                    to={client.referral_id ? `/referrals?open=${client.referral_id}` : `/clients/${client.id}`}
                    className="block p-4 rounded-lg border border-slate-100 hover:border-primary/30 hover:bg-slate-50 transition-colors"
                    data-testid={`potential-client-row-${client.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-medium">
                          {client.first_name[0]}
                          {client.last_name[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {client.first_name} {client.last_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {client.phone || "No phone"}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        client.workflow_step === "Insurance Denied" 
                          ? "bg-red-100 text-red-700"
                          : client.workflow_progress >= 70
                          ? "bg-emerald-100 text-emerald-700"
                          : client.workflow_progress >= 40
                          ? "bg-blue-100 text-blue-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {client.workflow_step}
                      </span>
                    </div>
                    {/* Progress Bar */}
                    <div className="relative">
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 rounded-full ${
                            client.workflow_step === "Insurance Denied"
                              ? "bg-red-500"
                              : client.workflow_progress >= 70
                              ? "bg-emerald-500"
                              : client.workflow_progress >= 40
                              ? "bg-blue-500"
                              : "bg-amber-500"
                          }`}
                          style={{ width: `${client.workflow_progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-slate-400">Referral</span>
                        <span className="text-xs text-slate-400">Admitted</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Upcoming RN Visits/Assessments - visible only if user has schedule permission */}
          {hasPermission("schedule") && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="upcoming-visits-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Upcoming RN Visits</h2>
                  <p className="text-xs text-slate-500">Assessments & Supervisory Visits</p>
                </div>
                <Link to="/schedule" className="text-sm text-primary hover:underline">
                  View all
                </Link>
              </div>
              {upcomingAppointments.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-4">
                  No upcoming RN visits scheduled
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingAppointments.map((appt) => (
                    <div
                      key={appt.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-indigo-50"
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-100">
                        <Calendar className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          <span className="text-indigo-600">
                            [{appt.is_assessment ? 'Assessment' : 
                              appt.appointment_type === 'supervisory_visit' ? 'Supervisory' :
                              appt.appointment_type === 'follow_up' ? 'Follow-up' : 'RN Visit'}]
                          </span>{' '}
                          {appt.client_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {appt.scheduled_date} at {appt.scheduled_time}
                          {appt.employee_name && ` • ${appt.employee_name}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Status Breakdown - visible only if user has clients permission */}
          {hasPermission("clients") && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="client-status-section">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Client Status
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span className="text-sm text-slate-600">Active</span>
                  </div>
                  <span className="font-semibold text-slate-900">
                    {stats?.active_clients || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span className="text-sm text-slate-600">Potential</span>
                  </div>
                  <span className="font-semibold text-slate-900">
                    {stats?.potential_clients || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span className="text-sm text-slate-600">On Hold</span>
                  </div>
                  <span className="font-semibold text-slate-900">
                    {stats?.on_hold_clients || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                    <span className="text-sm text-slate-600">Discharged</span>
                  </div>
                  <span className="font-semibold text-slate-900">
                    {stats?.discharged_clients || 0}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* QuickBooks Export Section - visible only if user has HR/Payroll permission */}
          {hasPermission("hr_payroll") && <QuickBooksExportSection />}

          {/* Communications Section - visible to users with communications permission */}
          {hasPermission("communications") && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="communications-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Communications
                </h2>
                <Link to="/communication">
                  <Button variant="ghost" size="sm" className="text-primary">
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
              
              <div className="space-y-3">
                {/* Quick Communication Actions */}
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col items-center gap-1 h-auto py-3"
                    onClick={() => navigate("/communication")}
                    data-testid="quick-email-btn"
                  >
                    <Mail className="w-4 h-4 text-blue-600" />
                    <span className="text-xs">Email</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col items-center gap-1 h-auto py-3"
                    onClick={() => toast.info("SMS feature - configure Twilio in Settings")}
                    data-testid="quick-sms-btn"
                  >
                    <Phone className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs">SMS</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col items-center gap-1 h-auto py-3"
                    onClick={() => toast.info("Notifications coming soon")}
                    data-testid="quick-notify-btn"
                  >
                    <Bell className="w-4 h-4 text-amber-600" />
                    <span className="text-xs">Notify</span>
                  </Button>
                </div>

                {/* Recent Activity Summary */}
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-2">Recent Activity</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Emails sent today</span>
                      <span className="font-medium text-slate-900">0</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">SMS sent today</span>
                      <span className="font-medium text-slate-900">0</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Pending notifications</span>
                      <span className="font-medium text-amber-600">0</span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-slate-400 mt-2">
                  Configure email and SMS in Settings → Integrations
                </p>
              </div>
            </div>
          )}

          {/* EVV Section - visible to users with EVV permission */}
          {hasPermission("evv") && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="evv-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Fingerprint className="w-5 h-5 text-cyan-600" />
                  EVV (Electronic Visit Verification)
                </h2>
                <Link to="/evv">
                  <Button variant="ghost" size="sm" className="text-primary">
                    Open EVV <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <Link to="/evv">
                  <Button
                    variant="outline"
                    className="w-full flex flex-col items-center gap-1 h-auto py-3"
                    data-testid="evv-clock-in-btn"
                  >
                    <Clock className="w-5 h-5 text-emerald-600" />
                    <span className="text-xs">Clock In/Out</span>
                  </Button>
                </Link>
                <Link to="/evv?tab=visits">
                  <Button
                    variant="outline"
                    className="w-full flex flex-col items-center gap-1 h-auto py-3"
                    data-testid="evv-visits-btn"
                  >
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="text-xs">View Visits</span>
                  </Button>
                </Link>
              </div>

              {/* EVV Stats Summary */}
              <div className="mt-4 p-3 bg-cyan-50 rounded-lg">
                <p className="text-xs text-cyan-700 mb-2">Today&apos;s EVV Status</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-cyan-600">Active visits</span>
                    <span className="font-medium text-cyan-900">{stats?.active_visits || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-cyan-600">Completed today</span>
                    <span className="font-medium text-cyan-900">{stats?.completed_visits_today || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions - Show based on permissions */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Quick Actions
            </h2>
            <div className="space-y-3">
              {hasPermission("clients", "create") && (
                <Link to="/clients/new" className="block">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    data-testid="quick-add-client-btn"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add New Client
                  </Button>
                </Link>
              )}
              {hasPermission("appointments", "create") && (
                <Link to="/schedule" className="block">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    data-testid="quick-schedule-btn"
                  >
                    <Calendar className="w-4 h-4" />
                    Schedule Visit
                  </Button>
                </Link>
              )}
              {hasPermission("employees") && (
                <Link to="/employees" className="block">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    data-testid="quick-view-employees-btn"
                  >
                    <Users className="w-4 h-4" />
                    View Employees
                  </Button>
                </Link>
              )}
              {hasPermission("referrals") && (
                <Link to="/referrals" className="block">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    data-testid="quick-referrals-btn"
                  >
                    <FileText className="w-4 h-4" />
                    View Referrals
                  </Button>
                </Link>
              )}
              {hasPermission("evv") && (
                <Link to="/evv" className="block">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    data-testid="quick-evv-btn"
                  >
                    <Fingerprint className="w-4 h-4" />
                    EVV Dashboard
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
